import { PlayerRef } from '@remotion/player';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { isEmpty } from 'radash';
import { Transition } from '@headlessui/react';
import { ElementType } from '@goldcast/player-config/src/types/Element/Type';
import {
  ElementPositions,
  SelectionElementType
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { Element } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/Element';
import {
  getAllCompositeSegments,
  getClipTranscriptTitle,
  isClipAudiogram,
  isFullLengthClip,
  isSplitsInTimelineEnabledPure,
  shouldShowLowResolutionPreview
} from '@goldcast/player-config/src/utils/clipUtils';
import { getPlayerTimeFromAbsoluteTimePure } from '@goldcast/player-config/src/utils/timeUtils';
import { ResponsiveContainer } from '../../../../components/atoms/ResponsiveContainer';
import { getClipPlayerElementId } from '../../../../Pages/Clip/ClipPlayer/ClipPlayerUtils';
import { RemotionPlayerContext } from './context/RemotionPlayerContext';
import { useUpdateCurrentSrtIndex } from './hooks/useUpdateCurrentSrtIndex';
import { playerPlaybackRateStore } from '../stores/playerPlaybackRateStore';
import ClipPlayerControlsV2 from '../ClipPlayerControlsV2/ClipPlayerControlsV2';
import LowResolutionPreviewTag from '../LowResolutionPreviewTag/LowResolutionPreviewTag';
import RemotionPlayerOnly from './RemotionPlayerOnly';
import { useRemotionTimeSync } from './hooks/useRemotionTimeSync';
import { useTrackPlayerEvents } from './hooks/useTrackPlayerEvents';
import useRemotionPauseOnPlayerMenu from './hooks/useRemotionPauseOnPlayerMenu';
import PlayerMenus from '@/App/remotion/components/PlayerMenus/PlayerMenus';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import ClipHeader from '@/components/molecules/ClipHeader';
import Icon from '@/components/atoms/Icon';
import { useEditorNavSlot } from '@/context/EditorNavSlotContext/EditorNavSlotContext';
import ClipCustomizerMenu from '@/Pages/Clip/ClipPlayer/ClipCustomizer/ClipCustomizerMenu';
import ViewModeBanner from '@/Pages/Clip/ClipPlayer/ViewModeBanner';
import useEditorRestrictions from '@/hooks/useEditorRestrictions';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import FullTranscript from '@/components/molecules/Transcript/FullTranscript';
import { changeCaptionsPosition, isLayoutsWithVisionDataEnabled, isSplitsFeatureEnabled } from '@/stores/clip';
import { ClipNewTimeline } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline';
import {
  TIMELINE_DISPLAY_TYPE,
  TRANSCRIPT_DISPLAY_TYPE
} from '@/Pages/Clip/ClipPlayer/ClipTimelineV2/ClipTimelineTypes';
import {
  getPlayerTimeFromSegmentTime,
  getPreviousSegmentAbsoluteTime
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';
import { formatTime } from '@/libs/core';
import { changeElementPositionHandler } from '@/Pages/Clip/ClipPlayer/ClipAssets/handlers';
import { setCurrentTime } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import usePlayerAspectRatio from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/hooks/usePlayerAspectRatio';
import AnalysisOverlay from '@/Pages/Clip/ClipPlayer/AnalysisOverlay';
import FailedOverlay from '@/Pages/Clip/ClipPlayer/FailedOverlay';
import UnavailableOverlay from '@/Pages/Clip/ClipPlayer/UnavailableOverlay';
import { AgenticChatLoader } from '@/components/organisms/AgenticChatLoader/AgenticChatLoader';
import { aiEditsStore, AI_EDITS_SIDEBAR_MIN_WIDTH, AI_EDITS_SIDEBAR_MAX_WIDTH } from '@/stores/aiEditsStore';
import { core } from '@/stores/core';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';

export default function ClipPlayerV2WithNewTimeline({
  playerClasses = '',
  canEditCaptions = false,
  showClipHeader = false,
  timelineDisplayType = TIMELINE_DISPLAY_TYPE.PROGRESS_BAR,
  transcriptDisplayType,
  isSharedPage = false,
  canEditSpeakerLabel = false,
  canEditImages = false,
  canEditTextElements = false,
  canEditVideos = false,
  canEditLottie = false
}: {
  playerClasses?: string;
  canEditCaptions?: boolean;
  showClipHeader?: boolean;
  timelineDisplayType?: TIMELINE_DISPLAY_TYPE;
  transcriptDisplayType?: TRANSCRIPT_DISPLAY_TYPE;
  isSharedPage?: boolean;
  canEditSpeakerLabel?: boolean;
  canEditImages?: boolean;
  canEditTextElements?: boolean;
  canEditVideos?: boolean;
  canEditLottie?: boolean;
}) {
  const remotionPlayerRef = useRef<PlayerRef>(null);
  const { clipId, clipData, fps, layoutStatus } = useClipsContext();
  const { onCreateClip } = useCreateClip({ clip: clipData });
  const playerConfig = clipData.asset_metadata.player_config;
  const playbackRate = useSyncExternalStore(playerPlaybackRateStore.subscribe, playerPlaybackRateStore.getSnapshot);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const aiEditsState = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);
  const { navSlotEl, setHasNav } = useEditorNavSlot();

  // Tell the layout a top nav is mounted (so it drops the back button from the left rail).
  // useLayoutEffect runs before paint, avoiding a one-frame flash of the rail back button.
  useLayoutEffect(() => {
    if (showClipHeader && navSlotEl) {
      setHasNav(true);
      return () => setHasNav(false);
    }
    return undefined;
  }, [showClipHeader, navSlotEl, setHasNav]);

  useUpdateCurrentSrtIndex({
    playerRef: remotionPlayerRef
  });

  useTrackPlayerEvents({
    playerRef: remotionPlayerRef,
    fps,
    clipId
  });

  const { isBlocked } = useEditorRestrictions();
  const isElementManipulationBlocked = isBlocked('element-customizer');

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const showPlayerFrames = featureFlags[FeatureFlagKeys.Use_CL_Show_Player_Frames] === true;
  const splitsInTimelineEnabled = isSplitsFeatureEnabled(featureFlags, clipData);
  const { compositionHeight, compositionWidth } = usePlayerAspectRatio({ size: clipData.asset_metadata.size });

  const onOutlineRelease = useCallback(
    (positions: ElementPositions, element?: Element) => {
      if (
        element?.type === ElementType.IMAGE ||
        element?.type === ElementType.VIDEO ||
        element?.type === ElementType.TEXT
      ) {
        changeElementPositionHandler(clipData, clipId, element.id, {
          containerStyle: positions,
          style: element.style || {}
        });
        return;
      }

      changeCaptionsPosition({
        clipId,
        caption_positions: {
          x: positions.left as any,
          y: positions.top as any,
          width: positions.width as any,
          height: positions.height as any
        },
        skipUpdate: false
      });
    },
    [clipId, clipData]
  );

  const onOutlineResize = useCallback(
    (positions: ElementPositions, selectionElementType: SelectionElementType) => {
      if (selectionElementType === SelectionElementType.CAPTIONS) {
        changeCaptionsPosition({
          clipId,
          caption_positions: {
            x: positions.left as any,
            y: positions.top as any,
            width: positions.width as any,
            height: positions.height as any
          },
          skipUpdate: true
        });
      }
    },
    [clipId]
  );
  /**
   * Includes the trim offset logic for the composite segment.
   */
  const playMainSectionFromTime = useCallback(
    (time: number, shouldPlay?: boolean) => {
      const remotionPlayer = remotionPlayerRef.current;
      const isPlaying = remotionPlayer?.isPlaying();
      if (!remotionPlayer) {
        return;
      }
      // !!TODO @HiteshChoudhary0098: Handle splits logic here
      const compositeSegments = getAllCompositeSegments(clipData) || [];

      if (compositeSegments.length === 0) {
        return;
      }
      const compositeSegment = compositeSegments[0];

      const previousSegmentAbsoluteTime = getPreviousSegmentAbsoluteTime(compositeSegment, clipData, -Infinity);
      const seekTime = getPlayerTimeFromAbsoluteTimePure({
        absoluteTime: time + previousSegmentAbsoluteTime,
        clipData,
        minSegmentWidth: -Infinity,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });

      setCurrentTime(seekTime, fps, remotionPlayer);
      if (shouldPlay && !isPlaying) {
        remotionPlayer.play();
      } else {
        remotionPlayer.pause();
      }
    },
    [clipData, fps]
  );

  // Move this into FullTranscript after removal of Use_CL_New_Recording_Studio_Timeline
  const pausePlayer = useCallback(() => {
    remotionPlayerRef.current?.pause();
  }, []);

  const handleAIEditsSidebarClose = useCallback(() => {
    window.analytics?.track('ContentLabEvent', {
      name: 'AIEditsSidebarClosed',
      trigger: aiEditsState.sidebarOpenTrigger || 'Unknown',
      hadInteraction: aiEditsState.hadInteraction,
      clipId: clipId,
      mediaType: coreStore.content?.media_type
    });

    aiEditsStore.setIsAIEditsSidebarOpen(false);
  }, [aiEditsState, clipId, coreStore.content]);

  const handleSidebarResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Function declarations (hoisted) so onMouseMove and stopResize can reference each other.
    function stopResize() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stopResize);
      document.body.style.userSelect = '';
      // Re-enable the width transition now that the drag has ended.
      aiEditsStore.setIsResizingSidebar(false);
    }
    function onMouseMove(ev: MouseEvent) {
      // If the primary button is no longer held (e.g. released outside the window), stop resizing.
      if (ev.buttons === 0) {
        stopResize();
        return;
      }
      // Sidebar is anchored to the right edge; width grows as the pointer moves left.
      aiEditsStore.setSidebarWidth(window.innerWidth - ev.clientX);
    }
    document.body.style.userSelect = 'none';
    // Drop the width transition so the editor reflows in real time with the drag
    // (the transition is only wanted for the open/close slide).
    aiEditsStore.setIsResizingSidebar(true);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopResize);
  }, []);

  // Keyboard resize for the separator: Left/Right (and Home/End) adjust the sidebar width.
  // The sidebar is anchored right, so ArrowLeft widens it and ArrowRight narrows it.
  const handleSidebarResizeKeyDown = useCallback((e: React.KeyboardEvent) => {
    const STEP = 16;
    const current = aiEditsStore.getSnapshot().sidebarWidth;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        aiEditsStore.setSidebarWidth(current + STEP);
        break;
      case 'ArrowRight':
        e.preventDefault();
        aiEditsStore.setSidebarWidth(current - STEP);
        break;
      case 'Home':
        e.preventDefault();
        aiEditsStore.setSidebarWidth(AI_EDITS_SIDEBAR_MAX_WIDTH);
        break;
      case 'End':
        e.preventDefault();
        aiEditsStore.setSidebarWidth(AI_EDITS_SIDEBAR_MIN_WIDTH);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    const visibilityChangeHandler = () => {
      if (document.visibilityState !== 'visible') {
        remotionPlayerRef.current?.pause();
      }
    };

    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };
  }, []);

  const getRenderTime = useCallback(
    (segmentTime: number, segmentId: string) => {
      return formatTime(getPlayerTimeFromSegmentTime(segmentTime, clipData, segmentId));
    },
    [clipData]
  );

  const { disableCaptionsDragging, disableCaptionsResize } = useMemo(() => {
    const isAudiogramClip = isClipAudiogram(clipData);

    return {
      disableCaptionsDragging: isAudiogramClip,
      disableCaptionsResize: isAudiogramClip
    };
  }, [clipData]);

  useRemotionTimeSync({ playerRef: remotionPlayerRef.current });

  const isFullClip = useMemo(() => {
    return isFullLengthClip(clipData);
  }, [clipData]);

  useRemotionPauseOnPlayerMenu({ remotionPlayerRef });

  const [showSpeakerLayoutProcessingOverlay, showSpeakerLayoutFailedOverlay, showSpeakerLayoutUnavailableOverlay] =
    useMemo(
      () => [layoutStatus === 'PROCESSING', layoutStatus === 'FAILED', layoutStatus === 'UNAVAILABLE'],
      [layoutStatus]
    );

  const { isAIEditsSidebarOpen } = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabledPure({
      clip: clipData,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
  }, [clipData]);
  const isClipDuplicateEnabled = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Clip_Duplication];

  useEffect(() => {
    if (isSplitsEnabled && isAIEditsSidebarOpen && !splitsInTimelineEnabled) {
      handleAIEditsSidebarClose();
    }
  }, [isSplitsEnabled, isAIEditsSidebarOpen, splitsInTimelineEnabled, handleAIEditsSidebarClose]);

  return (
    <RemotionPlayerContext.Provider value={{ playerRef: remotionPlayerRef.current }}>
      <div
        className="flex size-full"
        style={{
          maxWidth: isAIEditsSidebarOpen ? `calc(100vw - 84px - ${aiEditsState.sidebarWidth}px)` : 'calc(100vw - 84px)',
          width: isAIEditsSidebarOpen ? `calc(100vw - 84px - ${aiEditsState.sidebarWidth}px)` : 'calc(100vw - 84px)',
          paddingRight: 0,
          // No transition while dragging the resize handle, so the editor reflows in real time;
          // keep the smooth animation for the open/close slide.
          transition: aiEditsState.isResizingSidebar
            ? 'none'
            : 'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), padding-right 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'max-width, padding-right',
          // Reserve exactly the customizer rail width (its buttons are w-16 / 64px) so the rail
          // hugs its icons — no extra right gutter — and stays visible when Co-edit is open.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          '--clip-customizer-menu-width': '68px'
        }}
      >
        <div
          id={getClipPlayerElementId(clipId)}
          className={
            'flex max-w-[calc(100%-var(--clip-customizer-menu-width))] flex-1 flex-col items-center justify-center'
          }
        >
          <ViewModeBanner />
          <div className="flex size-full flex-row items-center justify-between">
            {transcriptDisplayType === TRANSCRIPT_DISPLAY_TYPE.FULL ? (
              <div
                className={classnames(
                  'relative h-full',
                  isAIEditsSidebarOpen
                    ? 'max-xl:w-0 max-xl:min-w-0 max-xl:overflow-hidden xl:w-[30%] xl:min-w-[calc(24rem)] xl:max-w-[calc(34rem)]'
                    : 'w-[30%] min-w-[calc(24rem)] max-w-[calc(34rem)]'
                )}
              >
                <FullTranscript
                  videoAssetId={clipId}
                  title={getClipTranscriptTitle(clipData, isClipDuplicateEnabled)}
                  onCreateClip={onCreateClip}
                  onPause={pausePlayer}
                  fullClipOffset={0}
                  disableRedirectAfterCreateClip
                  getRenderTime={getRenderTime}
                  hideEndTimeStamp
                />
              </div>
            ) : (
              <></>
            )}
            <div
              className={classnames(
                'flex h-full flex-col',
                transcriptDisplayType === TRANSCRIPT_DISPLAY_TYPE.FULL ? 'flex-1' : 'w-full'
              )}
            >
              {showClipHeader &&
                (navSlotEl ? (
                  createPortal(<ClipHeader newPlayer={true} />, navSlotEl)
                ) : (
                  <ClipHeader newPlayer={true} />
                ))}
              <div
                className={classnames(
                  'flex flex-1 items-center justify-center rounded-md p-4',
                  // The element toolbar (PlayerMenus) is pinned 54px above the composition. When the
                  // top nav is shown it sits directly above the player, so add top clearance to keep
                  // that toolbar out of the nav band, and trim the bottom to offset the extra space.
                  // Scoped to the nav view so other embeds keep their original padding.
                  { 'pb-1 pt-10': showClipHeader },
                  playerClasses
                )}
              >
                <ResponsiveContainer
                  id="player-main-section"
                  compositionHeight={compositionHeight}
                  compositionWidth={compositionWidth}
                  parentHeight={'90%'}
                  parentWidth={'90%'}
                  childClassnames="m-auto"
                >
                  {showSpeakerLayoutProcessingOverlay ? (
                    <AnalysisOverlay />
                  ) : showSpeakerLayoutFailedOverlay ? (
                    <FailedOverlay />
                  ) : showSpeakerLayoutUnavailableOverlay ? (
                    <UnavailableOverlay />
                  ) : (
                    <>
                      <div className="absolute inset-x-0 top-[-54px] z-[60]">
                        <PlayerMenus />
                      </div>
                      {/* ! TODO: @AshwinBhatkal to handle the state updates better */}
                      {playerConfig && !isEmpty(playerConfig) && (
                        <RemotionPlayerOnly
                          clipId={clipId}
                          remotionPlayerRef={remotionPlayerRef}
                          playerConfig={playerConfig}
                          durationInFrames={playerConfig.durationInFrames || 0}
                          compositionHeight={compositionHeight}
                          compositionWidth={compositionWidth}
                          playbackRate={playbackRate}
                          showPlayerFrames={showPlayerFrames}
                          canEditCaptions={canEditCaptions && !isElementManipulationBlocked}
                          canEditImages={canEditImages && !isElementManipulationBlocked}
                          canEditTextElements={canEditTextElements && !isElementManipulationBlocked}
                          canEditVideos={canEditVideos && !isElementManipulationBlocked}
                          canEditLottie={canEditLottie && !isElementManipulationBlocked}
                          canManuallyReposition={!isElementManipulationBlocked}
                          onOutlineRelease={onOutlineRelease}
                          onOutlineResize={onOutlineResize}
                          isSharedPage={isSharedPage}
                          canEditSpeakerLabel={canEditSpeakerLabel && !isElementManipulationBlocked}
                          disableCaptionsDragging={disableCaptionsDragging}
                          disableCaptionsResize={disableCaptionsResize}
                          fps={fps}
                          preventOverlappingWithOtherCrops={isLayoutsWithVisionDataEnabled(clipData)}
                        />
                      )}
                      <LowResolutionPreviewTag isVisible={shouldShowLowResolutionPreview(clipData)} />
                    </>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          {timelineDisplayType === TIMELINE_DISPLAY_TYPE.PROGRESS_BAR ? <ClipPlayerControlsV2 /> : <ClipNewTimeline />}
        </div>
        <div className="pointer-events-auto visible">
          <ClipCustomizerMenu
            isFullRecordingEdit={isFullClip}
            onTimeUpdate={playMainSectionFromTime}
            useNewRecordingsPlayer
          />
        </div>
      </div>
      <Transition
        show={isAIEditsSidebarOpen}
        enter="transition-all duration-300"
        enterFrom="translate-x-full opacity-0"
        enterTo="translate-x-0 opacity-100"
        leave="transition-all duration-300"
        leaveFrom="translate-x-0 opacity-100"
        leaveTo="translate-x-full opacity-0"
        className="fixed inset-y-0 right-0 z-[65] p-2"
        style={{
          width: `${aiEditsState.sidebarWidth}px`,
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Co-edit panel"
          aria-valuenow={Math.round(aiEditsState.sidebarWidth)}
          aria-valuemin={AI_EDITS_SIDEBAR_MIN_WIDTH}
          aria-valuemax={AI_EDITS_SIDEBAR_MAX_WIDTH}
          tabIndex={0}
          data-testid="ai-edits-resize-handle"
          onMouseDown={handleSidebarResizeStart}
          onKeyDown={handleSidebarResizeKeyDown}
          className="group absolute inset-y-0 left-0 z-10 flex w-3 cursor-ew-resize items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-orange"
        >
          {/* Full-height divider line — subtle by default, highlights on hover */}
          <div className="h-full w-px bg-slate-200 transition-all duration-150 group-hover:w-0.5 group-hover:bg-deep-orange" />
          {/* Drag affordance — appears on hover so the resize is discoverable */}
          <div className="absolute flex h-7 w-4 items-center justify-center rounded-md border border-slate-200 bg-white opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100">
            <Icon name="IconGripVertical" className="size-3.5 text-slate-500" stroke={2} />
          </div>
        </div>
        <AgenticChatLoader onClose={handleAIEditsSidebarClose} />
      </Transition>
    </RemotionPlayerContext.Provider>
  );
}
